<template>
  <a-select
    show-search
    :value="field.value"
    :disabled="disabled || loading"
    :placeholder="config.placeholder || t('fontFamily.choose')"
    class="font-select"
    dropdownClassName="props-font-select"
    :class="field.value"
    :dropdownMatchSelectWidth="false"
    @change="onChange"
  >
    <a-select-opt-group v-if="customFontList.length" class="mb-2">
      <template #label>
        <div class="group-name"><UserOutlined class="mr-2" />{{ t('propsEditor.customFont') }}</div>
      </template>
      <a-select-option
        :class="option.fontPreviewUrl ? '' : option.fontName"
        v-for="option in customFontList"
        :key="option.fontName"
        :value="option.fontName"
        :label="option.fontName"
      >
        <div class="selected-label text-3 gray9 flex justify-between items-center">
          <span class="mr-2">{{ option.fontNameOrigin }}</span>
          <a-tooltip :title="option.lang?.map(i => t(`language.${i}`) || i).join('/')" placement="topRight">
            <a-tag size="small" color="orange" v-if="option.lang?.length">
              {{ t(`language.${option.lang[0]}`) }}{{ option.lang.length > 1 ? '...' : '' }}
            </a-tag>
          </a-tooltip>
        </div>
        <div class="pb-1 pt-2">
          <img v-if="option.fontPreviewUrl" :src="option.fontPreviewUrl" style="height: 16px" />
          <template v-else>{{ option.fontName }}</template>
        </div>
      </a-select-option>
    </a-select-opt-group>
    <a-select-opt-group>
      <template #label>
        <div class="group-name">{{ t('propsEditor.systemFont') }}</div>
      </template>
      <a-select-option
        :class="option.preview ? '' : option.value"
        v-for="option in list"
        :key="option.value"
        :value="option.value"
        :label="option.label"
      >
        <div class="selected-label text-3 gray9 flex justify-between items-center">
          <span class="mr-2">{{ option.font }}</span>
          <a-tag size="small" color="orange" v-if="option.tip">{{ option.tip }}</a-tag>
        </div>
        <div class="pb-1 pt-2">
          <img v-if="option.preview" :src="`https://i.tosoiot.com/static/font${option.preview}`" style="height: 16px" />
          <template v-else>{{ option.label }}</template>
        </div>
      </a-select-option>
    </a-select-opt-group>
  </a-select>
</template>
<script>
import { props } from './helper';
import { ref, defineComponent, computed } from 'vue';
import { setTargetLangManualFont } from '@/utils/ls';
import { loadFonts, addFontFace } from '@/utils';
import { useStore } from 'vuex';
import { UserOutlined } from '@ant-design/icons-vue';

import i18n from '@/locales/i18n';

export default defineComponent({
  props,
  components: { UserOutlined },
  setup(props) {
    const store = useStore();
    const { t } = i18n.global;
    const customFontList = computed(() => store.state.user.customFontList || []);
    const customFontMap = computed(() => store.getters['user/customFontMap']);
    const list = computed(() => {
      const all = [
        {
          value: 'ff-GmarketSansMedium',
          preview: '/korean/GmarketSansMedium.png',
          font: 'GmarketSansMedium',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-GowunBatang-Regular',
          preview: '/korean/GowunBatang-Regular.png',
          font: 'GowunBatang',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-HarmonyOS_Sans_SC_Regular',
          preview: '/harmonyOS/HarmonyOS_Sans_SC_Regular.png',
          font: '简体-鸿蒙',
          label: '你好吗',
          lang: ['CHS', 'CHT'],
        },
        {
          value: 'ff-HarmonyOS_Sans_TC_Regular',
          preview: '/harmonyOS/HarmonyOS_Sans_TC_Regular.png',
          font: '繁体-鴻蒙',
          label: '你好嗎',
          lang: ['CHS', 'CHT'],
        },
        {
          value: 'ff-houzunsongti',
          preview: '/cht/houzunsongti.png',
          font: '繁体-猴尊宋体',
          label: '你好嗎',
          lang: ['CHS', 'CHT'],
        },
        {
          value: 'ff-lishapop',
          preview: '/cht/lishapop.png',
          font: '繁体-梨沙POP体',
          label: '你好嗎',
          lang: ['CHS', 'CHT'],
        },
        {
          value: 'ff-MantouSans-Regular',
          preview: '/cht/MantouSans-Regular.png',
          font: '繁体-馒头黑体',
          label: '你好嗎',
          lang: ['CHS', 'CHT'],
        },
        {
          value: 'ff-HarmonyOS_Sans_Regular',
          preview: '/harmonyOS/HarmonyOS_Sans_Regular.png',
          font: '鸿蒙 Sans',
          label: '你好吗',
          lang: ['CHS', 'CHT'],
        },
        {
          value: 'ff-FamiljenGrotesk-Regular',
          preview: '/japan/FamiljenGrotesk-Regular.png',
          font: 'GowunBatang',
          label: 'こんにちは',
          lang: ['JPN'],
        },
        {
          value: 'ff-ZenMaruGothic-Regular',
          preview: '/japan/ZenMaruGothic-Regular.png',
          font: 'Zen丸ゴシック',
          label: 'こんにちは',
          lang: ['JPN'],
        },
        {
          value: 'ff-ZenKakuGothicNew-Regular',
          preview: '/japan/ZenKakuGothicNew-Regular.png',
          font: 'Zen 角ゴシックNEW',
          label: 'こんにちは',
          lang: ['JPN'],
        },
        {
          value: 'ff-A-OTF-ShinMGoPro-Regular',
          preview: '/japan/A-OTF-ShinMGoPro-Regular.png',
          font: 'ShinMGoPro-Regular',
          label: 'こんにちは',
          lang: ['JPN'],
        },
        {
          value: 'ff-GenWanMinJP-Regular',
          preview: '/japan/GenWanMinJP-Regular.png',
          font: 'GenWanMinJP-Regular',
          label: 'こんにちは',
          lang: ['JPN'],
        },
        {
          value: 'ff-DB_HELVETHAICA_X_BD_COND',
          preview: '/thai/DB_HELVETHAICA_X_BD_COND.png',
          font: 'DB_HELVETHAICA_X_BD_COND',
          label: 'สวัสดี',
          lang: ['TH'],
        },
        {
          value: 'ff-DB_Helvethaica_X_Bd',
          preview: '/thai/DB_Helvethaica_X_Bd.png',
          font: 'DB_Helvethaica_X_Bd',
          label: 'สวัสดี',
          lang: ['TH'],
        },
        {
          value: 'ff-DB_HELVETHAICA_X_BLK_COND',
          preview: '/thai/DB_HELVETHAICA_X_BLK_COND.png',
          font: 'DB_HELVETHAICA_X_BLK_COND',
          label: 'สวัสดี',
          lang: ['TH'],
        },
        {
          value: 'ff-DB_Helvethaica_X_Blk',
          preview: '/thai/DB_Helvethaica_X_Blk.png',
          font: 'DB_Helvethaica_X_Blk',
          label: 'สวัสดี',
          lang: ['TH'],
        },
        {
          value: 'ff-DB_HELVETHAICA_X_MED_COND',
          preview: '/thai/DB_HELVETHAICA_X_MED_COND.png',
          font: 'DB_HELVETHAICA_X_MED_COND',
          label: 'สวัสดี',
          lang: ['TH'],
        },
        {
          value: 'ff-Cafe24Dangdanghae',
          preview: '/japan/Cafe24Dangdanghae.png',
          font: 'Cafe24Dangdanghae',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-NanumGothic-Regular',
          preview: '/korean/NanumGothic-Regular.png',
          font: 'NanumGothic',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-CookieRun-Regular',
          preview: '/korean/CookieRun-Regular.png',
          font: 'CookieRun',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-EastSeaDokdo-Regular',
          preview: '/korean/EastSeaDokdo-Regular.png',
          font: 'EastSeaDokdo',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-SingleDay-Regular',
          preview: '/korean/SingleDay-Regular.png',
          font: 'SingleDay',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-SongMyung-Regular',
          preview: '/korean/SongMyung-Regular.png',
          font: 'SongMyung',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-NanumSquareL',
          preview: '/korean/NanumSquareL.png',
          font: 'NanumSquareL',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-NanumSquareR',
          preview: '/korean/NanumSquareR.png',
          font: 'NanumSquareR',
          label: '안녕하세요.',
          lang: ['KOR'],
        },
        {
          value: 'ff-Roboto-Black',
          preview: '/english/Roboto-Black.png',
          font: 'Roboto-Black',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-Roboto-Regular',
          preview: '/english/Roboto-Regular.png',
          font: 'Roboto-Regular',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        // {
        //   value: 'ff-Roboto-Light',
        //   preview: '/english/Roboto-Light.png',
        //   font: 'Roboto-Light',
        //   label: 'hello',
        //   lang: [t('fontFamily.englishEtc')],
        // },
        {
          value: 'ff-RandomBoys',
          preview: '/english/RandomBoys.png',
          font: 'RandomBoys',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-NotoSans-Bold-5',
          preview: '/english/NotoSans-Bold-5.png',
          font: 'NotoSans-Bold-5',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-ABeeZee-Regular-2',
          preview: '/english/ABeeZee-Regular-2.png',
          font: 'ABeeZee-Regular-2',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-Abel-Regular-2',
          preview: '/english/Abel-Regular-2.png',
          font: 'Abel-Regular-2',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-AbhayaLibre-ExtraBold-2',
          preview: '/english/AbhayaLibre-ExtraBold-2.png',
          font: 'AbhayaLibre-ExtraBold-2',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-Alegreya-Italic-8',
          preview: '/english/Alegreya-Italic-8.png',
          font: 'Alegreya-Italic-8',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-ArchivoBlack-Regular-2',
          preview: '/english/ArchivoBlack-Regular-2.png',
          font: 'ArchivoBlack-Regular-2',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-Big-Snow-3',
          preview: '/english/Big-Snow-3.png',
          font: 'Big-Snow-3',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-Cabin-Medium-7',
          preview: '/english/Cabin-Medium-7.png',
          font: 'Cabin-Medium-7',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-Montserrat-Black-2',
          preview: '/english/Montserrat-Black-2.png',
          font: 'Montserrat-Black-2',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-PublicSans-Regular-14',
          preview: '/english/PublicSans-Regular-14.png',
          font: 'PublicSans-Regular-14',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-Ubuntu-M',
          preview: '/english/Ubuntu-M.png',
          font: 'Ubuntu-M',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-Montserrat-Medium',
          preview: '/english/Montserrat-Medium.png',
          font: 'Montserrat-Medium',
          label: 'hello',
          lang: [t('fontFamily.englishEtc')],
        },
        {
          value: 'ff-Cafe24SsurroundAir',
          preview: '/yunmeng/Cafe24SsurroundAir.png',
          font: 'Cafe24SsurroundAir',
          label: '안녕하세요',
          lang: ['KOR'],
        },
        {
          value: 'ff-Cafe24Ssurround',
          preview: '/yunmeng/Cafe24Ssurround.png',
          font: 'Cafe24Ssurround',
          label: '안녕하세요',
          lang: ['KOR'],
        },
        {
          value: 'ff-fangzhengfangsong',
          preview: '/cht/fangzhengfangsong.jpg',
          font: '方正仿宋',
          label: '方正仿宋',
          lang: ['CHT', 'CHS'],
        },
        {
          value: 'ff-fangzhengheiti',
          preview: '/cht/fangzhengheiti.jpg',
          font: '方正黑体',
          label: '方正黑体',
          lang: ['CHT', 'CHS'],
        },
        {
          value: 'ff-fangzhengkaiti',
          preview: '/cht/fangzhengkaiti.jpg',
          font: '方正楷体',
          label: '方正楷体',
          lang: ['CHT', 'CHS'],
        },
        {
          value: 'ff-qingsongshouxieti1',
          preview: '/cht/清松手写体1.jpg',
          font: '清松手写体',
          label: '清松手写体',
          lang: ['CHT', 'CHS'],
        },
        { value: 'Microsoft YaHei', font: '微软雅黑', label: '你好', lang: [t('fontFamily.chineseEtc')] },
      ];
      const supported = all.filter(i => i.lang.includes(props.globalData.targetLanguage));
      const unsupported = all.filter(i => !i.lang.includes(props.globalData.targetLanguage));

      const result = [
        { value: '', font: t('fontFamily.default'), label: t('fontFamily.example'), lang: [] },
        ...supported.map(i => ({ ...i, tip: t('fontFamily.recommend') })),
        ...unsupported,
      ];

      return result;
    });
    const map = list.value.reduce((map, item) => {
      map[item.value] = item;
      return map;
    }, {});
    const filterOption = (input, option) => {
      return option.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };
    const loading = ref(false);
    const onChange = async font => {
      const customFont = customFontMap.value[font];
      if (customFont) {
        const { fontUrl, fontName } = customFont;
        console.log(fontUrl);
        await addFontFace([fontName], customFontMap.value);
        props.handleChange(font);
        setTargetLangManualFont(props.globalData.targetLanguage, font);
      } else {
        const fontName = map[font].value;
        console.log('font', font, fontName);
        if (fontName) {
          await loadFonts([fontName]);
        }
        props.handleChange(font);
        setTargetLangManualFont(props.globalData.targetLanguage, font);
        return;
      }
    };
    return {
      t,
      list,
      loading,
      onChange,
      filterOption,
      customFontList,
    };
  },
});
</script>
<style lang="less" scoped>
.group-name {
  position: relative;
  margin: 0 -8px;
  padding: 8px 12px;
  color: #222;
  font-size: 14px;
  font-weight: bold;
  border-radius: 4px;
  background-color: #eee;
  &::before {
    position: absolute;
    content: '';
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    border-radius: 3px;
    background-color: @primary-color;
  }
}
.font-select {
  ::v-deep(.ant-select-selection-item) {
    .selected-label {
      height: 100%;
      line-height: 30px;
      color: rgba(0, 0, 0, 0.85);
      overflow: hidden;
    }
    .ant-tag {
      display: none;
    }
  }
}
</style>
<style lang="less">
.props-font-select {
  width: 288px;
  .selected-label {
    overflow: hidden;
    line-height: 18px;
    white-space: normal;
    word-break: break-all;
  }
  .ant-select-item {
    border-bottom: 1px solid #eee;
  }
  .ant-tag {
    border: 0 none;
    margin: 0;
    padding: 0 2px;
    height: 18px;
    line-height: 18px;
  }
}
</style>
